import React, { useEffect, useState } from "react";

import { IScreeningRunSummary } from "../../services/zeos-api/contracts/Screening";

// Protocol §11 ask 3 (QT-3): a result header block stamped with run id/guid, timestamps, engine
// build, and definition versions -- so a tester can tell a cached result from a fresh one and tie
// a bug report to a specific build. Shared between Screening.tsx and ScreeningSandbox.tsx, both of
// which already poll their liveRun on the same cadence, so this just re-renders with fresh props.
//
// Protocol §2 / §11 ask 7 (QT-4): screening results are confirmed NOT cached anywhere in the
// pipeline (see zeos-api/AGENTS.md "Screening BES/ReWire runtime flow" -> Caching). The badge
// below states that plainly rather than adding a cache-hit indicator/force-refresh control, which
// would only apply to the caching branch this investigation ruled out.

interface IProps {
  run: IScreeningRunSummary;
}

const SLOW_RUN_THRESHOLD_SECONDS = 90;

export const ScreeningRunHeader: React.FC<IProps> = ({ run }) => {
  const [liveElapsedSeconds, setLiveElapsedSeconds] = useState<number | null>(null);
  const [showDefinitions, setShowDefinitions] = useState(false);

  // While the run hasn't reported a final duration yet, tick a local elapsed-time clock off
  // startedAtUtc so the duration is live, not just a value that appears once the run completes.
  useEffect(() => {
    if (run.runDurationSeconds != null || !run.startedAtUtc) {
      setLiveElapsedSeconds(null);
      return undefined;
    }
    const startedMs = new Date(run.startedAtUtc).getTime();
    const tick = () => setLiveElapsedSeconds((Date.now() - startedMs) / 1000);
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [run.runDurationSeconds, run.startedAtUtc]);

  const durationSeconds = run.runDurationSeconds ?? liveElapsedSeconds;
  const isSlow = run.isSlowRun ?? (durationSeconds != null && durationSeconds > SLOW_RUN_THRESHOLD_SECONDS);
  const definitionEntries = Object.entries(run.definitionVersions ?? {});

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          <span className="font-semibold">Run</span> #{run.runId ?? run.screeningRunId}
        </span>
        <span className="font-mono">{run.runGuid}</span>
        <span>Started {run.startedAtUtc ? new Date(run.startedAtUtc).toLocaleString() : "-"}</span>
        <span>Completed {run.completedAtUtc ? new Date(run.completedAtUtc).toLocaleString() : "-"}</span>
        <span className={isSlow ? "font-semibold text-red-700" : ""}>
          Duration {durationSeconds != null ? `${durationSeconds.toFixed(1)}s` : "-"}
          {isSlow && " (over 90s)"}
        </span>
        <span>Engine {run.engineBuildVersion ?? "unknown"}</span>
        <span
          className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700"
          title="Confirmed: screening results are never cached against the property record or anywhere else in the pipeline (see zeos-api/AGENTS.md). An identical result after a changed input is a matching defect, not a stale cache."
        >
          Fresh run — not cached
        </span>
        {definitionEntries.length > 0 && (
          <button
            type="button"
            className="underline"
            onClick={() => setShowDefinitions((current) => !current)}
          >
            {definitionEntries.length} definitions resolved
          </button>
        )}
      </div>
      {showDefinitions && (
        <div className="mt-2 grid grid-cols-2 gap-1 border-t border-slate-200 pt-2 sm:grid-cols-3">
          {definitionEntries.map(([key, versionId]) => (
            <div key={key}>
              {key}: v{versionId}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
